import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, Percent, Calendar, Zap, Calculator } from "lucide-react";
interface NewCashbackStepProps {
  onComplete: () => void;
  onPrevious: () => void;
}
export const NewCashbackStep = ({
  onComplete,
  onPrevious
}: NewCashbackStepProps) => {
  const [isActive, setIsActive] = useState(true);
  const [percentage, setPercentage] = useState(5);
  const [multiplier, setMultiplier] = useState(3);
  const [expirationDays, setExpirationDays] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const calculateExample = () => {
    const purchaseValue = 100;
    const cashbackValue = purchaseValue * percentage / 100;
    const gastoMinimo = cashbackValue * multiplier;
    const metricaRetorno = ((gastoMinimo - cashbackValue) / cashbackValue) * 100;
    return {
      purchaseValue,
      cashbackValue,
      gastoMinimo,
      metricaRetorno
    };
  };
  const handleSave = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      // Primeiro, desativar todas as regras existentes
      const {
        error: deactivateError
      } = await supabase.from('cashback_rules').update({
        is_active: false
      }).eq('user_id', user.id);
      if (deactivateError) throw deactivateError;

      // Criar nova regra ativa
      const {
        error: insertError
      } = await supabase.from('cashback_rules').insert({
        user_id: user.id,
        percentage,
        multiplier,
        expiration_days: expirationDays,
        is_active: isActive,
        include_shipping: true
      });
      if (insertError) throw insertError;
      toast({
        title: "Cashback configurado",
        description: "Suas regras de cashback foram salvas com sucesso!"
      });
      onComplete();
    } catch (error: any) {
      console.error('Erro ao salvar cashback:', error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar as configurações de cashback. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const example = calculateExample();
  return <div className="space-y-6">
      <div className="text-center">
        <Percent className="w-8 h-8 mx-auto mt-1 mb-1 text-primary" />
        <h2 className="text-2xl font-semibold text-card-foreground mb-2">Configurar Cashback</h2>
        <p className="text-muted-foreground">
          Configure as regras do seu programa de cashback
        </p>
      </div>

      {/* Ativação do Cashback */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" />
                Programa de Cashback
              </CardTitle>
              <CardDescription>
                Ative ou desative seu programa de cashback
              </CardDescription>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </div>
        </CardHeader>
      </Card>

      {/* Configurações */}
      {isActive && <Card>
          <CardHeader>
            <CardTitle>Configurações do Cashback</CardTitle>
            <CardDescription>
              Defina as regras para o cálculo do cashback
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Percent className="w-4 h-4" />
                  Percentual (%)
                </Label>
                <Input type="number" min="0.1" max="100" step="0.1" value={percentage} onChange={e => setPercentage(Number(e.target.value))} />
                <p className="text-xs text-muted-foreground">% do valor da compra</p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Multiplicador
                </Label>
                <Input type="number" min="1" max="10" value={multiplier} onChange={e => setMultiplier(Number(e.target.value))} />
                <p className="text-xs text-muted-foreground">Define o fator para calcular gasto mínimo</p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Expiração (dias)
                </Label>
                <Input type="number" min="1" max="365" value={expirationDays} onChange={e => setExpirationDays(Number(e.target.value))} />
                <p className="text-xs text-muted-foreground">Dias para usar o cashback</p>
              </div>
            </div>

            {/* Exemplo de Cálculo */}
            <Card className="bg-muted/30">
              <CardHeader className="p-3 ">
                <CardTitle className="text-lg flex items-center gap-2 text-left">
                  <Calculator className="w-5 h-5" />
                  Exemplo de Cálculo
                </CardTitle>
              </CardHeader>
              <CardContent className="py-1">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Valor da compra:</span>
                    <span className="font-medium">R$ {example.purchaseValue.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cashback ({percentage}%):</span>
                    <span className="text-primary">R$ {example.cashbackValue.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Multiplicador:</span>
                    <span>x{multiplier}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gasto Mínimo:</span>
                    <span className="font-medium">R$ {example.gastoMinimo.toFixed(2)}</span>
                  </div>
                  <hr className="border-border" />
                  <div className="flex justify-between font-medium">
                    <span>Métrica de retorno:</span>
                    <span className="text-primary text-lg">{example.metricaRetorno.toFixed(0)}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>}

      {/* Navegação */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={onPrevious} className="flex-1">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <Button onClick={handleSave} disabled={isLoading} className="flex-1">
          {isLoading ? <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Salvando...
            </> : "Configurar e Continuar"}
        </Button>
      </div>
    </div>;
};